import React, { useRef, useState, useEffect } from 'react';
import {
    IonButton,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
    IonRadio,
    IonRadioGroup,
    IonTitle,
    IonToggle,
    IonToolbar,
    IonButtons,
    useIonModal,
    IonDatetime,
    IonDatetimeButton,
    IonPopover,
} from '@ionic/react';
import {
    arrowBack,
    banOutline,
    chevronForward,
    close,
    musicalNoteOutline,
    volumeHighOutline,
    addOutline,
} from 'ionicons/icons';
import { Swiper, SwiperSlide, SwiperClass } from 'swiper/react';
import 'swiper/css';
import '@ionic/react/css/ionic-swiper.css';

import styles from './AddDoorPolicyModal.module.css';
import { TransitPolicy, TransitPolicyRule, TransitPolicyRuleCriteria } from '../models/TransitPolicy';
import { EventTriggerSource } from '../models/DeviceEvent';
import { useDeviceTransitPolicyCreateMutation } from '../query/deviceTransitPoliciesQuery';
import { useRfidProfileQuery } from '../query/rfidProfileQuery';
import { getRfidDisplayLabel } from '../utils/rfidDisplay';
import useUX from '../utils/ux';
import SelectRfidModal from './SelectRfidModal';
import { modalController } from '@ionic/core';
import MicrochipItemLabel from '../components/MicrochipItemLabel';
import ModalFooter from '../components/ModalFooter';
import footerStyles from '../components/ModalFooter.module.css';
import RemoveIconButton from '../components/RemoveIconButton';
import JsonViewerModal from './JsonViewerModal';
import { useDeveloperModeQuery } from '../query/developerModeQuery';
import { useTranslation } from '../hooks/useTranslation';

enum PAGES {
    POLICY_TYPE = 0,
    POLICY_NAME = 1,
}

enum PERMISSION {
    ALLOW = 'allow',
    DENY = 'deny',
    ALLOWLIST = 'allowlist',
}

interface PolicyTemplate {
    icon: string;
    description: string;
    defaults: {
        policyName: string;
        transitPolicy: TransitPolicy;
    };
}

const POLICY_TEMPLATE: { [key: string]: PolicyTemplate } = {
    UNLOCK_FOR_ALL: {
        icon: '/assets/lock/lock-unlocked.svg',
        description: 'add_door_policy.policy_templates.unlock_for_all.description',
        defaults: {
            policyName: 'add_door_policy.policy_templates.unlock_for_all.name',
            transitPolicy: new TransitPolicy({
                rules: [
                    new TransitPolicyRule({
                        action: { lock: false },
                        criteria: { eventTriggerSource: EventTriggerSource.IndoorMotion },
                        description: 'Exit Rule',
                    }),
                    new TransitPolicyRule({
                        editorKey: 'contraband',
                        action: { lock: true, sound: 'alarm' },
                        criteria: {
                            eventTriggerSource: EventTriggerSource.OutdoorMotion,
                            eventClassification: [2, 3],
                        },
                        description: 'Contraband Rule',
                    }),
                    new TransitPolicyRule({
                        editorKey: 'entry',
                        action: { lock: false },
                        criteria: { eventTriggerSource: EventTriggerSource.OutdoorMotion },
                        description: 'Entry Rule',
                    }),
                ],
                idleLockBattery: false
            }),
        },
    },
    CUSTOM: {
        icon: '/assets/lock/lock-smart.svg',
        description: 'add_door_policy.policy_templates.custom.description',
        defaults: {
            policyName: 'add_door_policy.policy_templates.custom.name',
            transitPolicy: new TransitPolicy({
                rules: [
                    new TransitPolicyRule({
                        editorKey: 'exit',
                        action: { lock: false },
                        criteria: { eventTriggerSource: EventTriggerSource.IndoorMotion },
                        description: 'Exit Rule',
                    }),
                    new TransitPolicyRule({
                        editorKey: 'contraband',
                        action: { lock: true, sound: 'alarm' },
                        criteria: {
                            eventTriggerSource: EventTriggerSource.OutdoorMotion,
                            eventClassification: [2, 3],
                        },
                        description: 'Contraband Rule',
                    }),
                    new TransitPolicyRule({
                        editorKey: 'entry',
                        action: { lock: false },
                        criteria: { eventTriggerSource: EventTriggerSource.OutdoorMotion },
                        description: 'Entry Rule',
                    }),
                ],
            }),
        },
    },
    TRAINING: {
        icon: '/assets/lock/lock-unlocked.svg',
        description: 'add_door_policy.policy_templates.training.description',
        defaults: {
            policyName: 'add_door_policy.policy_templates.training.name',
            transitPolicy: new TransitPolicy({
                idleLock: false,
                rules: [
                    new TransitPolicyRule({
                        editorKey: 'contraband',
                        action: { lock: undefined, sound: undefined },
                        criteria: {
                            eventTriggerSource: EventTriggerSource.OutdoorMotion,
                            eventClassification: [2, 3],
                        },
                        description: 'Contraband Rule',
                    }),
                ],
            }),
        },
    },
    LOCKED: {
        icon: '/assets/lock/lock-locked.svg',
        description: 'add_door_policy.policy_templates.locked.description',
        defaults: {
            policyName: 'add_door_policy.policy_templates.locked.name',
            transitPolicy: new TransitPolicy({
                rules: [
                    new TransitPolicyRule({
                        action: { lock: true },
                    }),
                ],
            }),
        },
    },
};

interface AddDoorPolicyModalProps {
    deviceId: string;
    dismiss: (data?: string | null | undefined | number, role?: string) => void;
}

const AddDoorPolicyModal: React.FC<AddDoorPolicyModalProps> = ({ deviceId, dismiss }) => {
    const [slideIndex, setSlideIndex] = useState<number>(0);
    const swiperRef = useRef<SwiperClass | null>(null);
    const { presentResult } = useUX();
    const { t } = useTranslation('modals');

    const [policyName, setPolicyName] = useState<string>('');
    const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(null);
    const [transitPolicy, setTransitPolicy] = useState<TransitPolicy | null>(null);
    const mutation = useDeviceTransitPolicyCreateMutation(deviceId);
    const developerModeQuery = useDeveloperModeQuery();
    const developerMode = !!developerModeQuery.data;

    const selectPolicyTemplate = (templateKey: string) => {
        const template = POLICY_TEMPLATE[templateKey];
        swiperRef.current?.slideNext();
        setPolicyName(t(template.defaults.policyName));
        setTransitPolicy(template.defaults.transitPolicy);
        setSelectedTemplateKey(templateKey);
    };

    const createPolicy = (activate: boolean) => {
        presentResult(async () => {
            if (!transitPolicy) {
                throw new Error('Transit Policy is required');
            }
            const cleanedPolicy = cleanTransitPolicy(transitPolicy);
            await mutation.mutateAsync({
                deviceId,
                name: policyName,
                transitPolicy: cleanedPolicy,
                activate,
            });
            dismiss();
        }, t(activate ? 'add_door_policy.activated' : 'add_door_policy.created'));
    };

    function adjustEndTime(time: string): string {
        let [hoursStr, minutesStr] = time.split(':');
        let hours = parseInt(hoursStr, 10);
        let minutes = parseInt(minutesStr, 10);

        // Subtract one minute
        if (minutes === 0) {
            minutes = 59;
            if (hours === 0) {
                hours = 23;
            } else {
                hours -= 1;
            }
        } else {
            minutes -= 1
        }

        // Format back to 'HH:mm'
        const adjustedHoursStr = hours.toString().padStart(2, '0');
        const adjustedMinutesStr = minutes.toString().padStart(2, '0');

        return `${adjustedHoursStr}:${adjustedMinutesStr}`;
    }

    const cleanTransitPolicy = (policy: TransitPolicy): TransitPolicy => {
        // Deep copy to avoid mutating the original policy
        const policyCopy = JSON.parse(JSON.stringify(policy));

        // Remove undefined and null properties recursively
        const removeUndefined = (obj: any) => {
            if (Array.isArray(obj)) {
                obj.forEach(removeUndefined);
            } else if (typeof obj === 'object' && obj !== null) {
                Object.keys(obj).forEach((key) => {
                    if (obj[key] && typeof obj[key] === 'object') {
                        removeUndefined(obj[key]);
                    } else if (obj[key] === undefined || obj[key] === null) {
                        delete obj[key];
                    }
                });
            }
        };

        removeUndefined(policyCopy);

        // Remove disabled rules, rules with no actions, and internal state variables
        policyCopy.rules = policyCopy.rules
            ?.filter((rule: any) => {
                const hasAction = rule.action && Object.keys(rule.action).length > 0;
                return rule.enabled !== false && hasAction;
            })
            .map((rule: any) => {
                delete rule.editorKey;

                // Adjust the end time in timeRange to be exclusive of the last minute
                if (rule.criteria?.timeRange) {
                    const [start, end] = rule.criteria.timeRange.split('-');
                    const adjustedEnd = adjustEndTime(end);
                    rule.criteria.timeRange = `${start}-${adjustedEnd}`;
                }

                return rule;
            });

        return policyCopy;
    };

    const updateTransitPolicyRule = (
        editorKey: string,
        updateFn: (rule: TransitPolicyRule) => TransitPolicyRule
    ) => {
        setTransitPolicy((prevPolicy) => {
            if (!prevPolicy) return prevPolicy;
            return new TransitPolicy({
                ...prevPolicy,
                rules: prevPolicy.rules?.map((rule) =>
                    rule.editorKey === editorKey ? updateFn(rule) : rule
                ),
            });
        });
    };

    // Entry Settings
    const [entryRfidCodeList, setEntryRfidCodeList] = useState<string[]>([]);
    const [entryPermission, setEntryPermission] = useState<PERMISSION>(PERMISSION.DENY);
    const [presentSelectEntryRfidModal, dismissSelectEntryRfidModal] = useIonModal(SelectRfidModal, {
        deviceId,
        disabledRfidCodes: entryRfidCodeList,
        dismiss: (rfidCode: string, role: string) => {
            if (role === 'confirm') {
                addEntryRfidCode(rfidCode);
            }
            dismissSelectEntryRfidModal(rfidCode, role);
        },
    });

    const entryRule = transitPolicy?.rules?.find((rule) => rule.editorKey === 'entry');
    const hasEntryRule = !!entryRule;

    useEffect(() => {
        if (entryRule) {
            if (entryRule.enabled !== false) {
                if (entryRule.criteria?.rfidCode) {
                    setEntryPermission(PERMISSION.ALLOWLIST);
                    setEntryRfidCodeList(entryRule.criteria.rfidCode as string[]);
                } else {
                    setEntryPermission(PERMISSION.ALLOW);
                }
            } else {
                setEntryPermission(PERMISSION.DENY);
            }
        } else {
            setEntryPermission(PERMISSION.DENY);
        }
    }, [transitPolicy, entryRule]);

    const addEntryRfidCode = (rfidCode: string) => {
        setEntryRfidCodeList((prevList) => {
            const newList = [...prevList, rfidCode];
            if (entryPermission === PERMISSION.ALLOWLIST) {
                updateEntryRuleCriteria({ rfidCode: newList });
            }
            return newList;
        });
    };

    const removeEntryRfidCode = (rfidCode: string) => {
        setEntryRfidCodeList((prevList) => {
            const newList = prevList.filter((code) => code !== rfidCode);
            if (entryPermission === PERMISSION.ALLOWLIST) {
                updateEntryRuleCriteria({ rfidCode: newList });
            }
            return newList;
        });
    };

    const updateEntryRuleCriteria = (criteriaUpdate: Partial<TransitPolicyRule['criteria']>) => {
        updateTransitPolicyRule('entry', (rule) => ({
            ...rule,
            criteria: {
                ...rule.criteria,
                ...criteriaUpdate,
            },
        }));
    };

    const setEntryPermissionState = (permission: PERMISSION) => {
        setEntryPermission(permission);
        updateTransitPolicyRule('entry', (rule) => {
            let criteria = { ...rule.criteria };
            if (permission === PERMISSION.ALLOWLIST) {
                criteria.rfidCode = entryRfidCodeList;
            } else {
                delete criteria.rfidCode;
            }
            return {
                ...rule,
                criteria,
                enabled: permission !== PERMISSION.DENY,
            };
        });
    };

    const hasEntryChime = !!transitPolicy?.rules?.find(
        (rule) => rule.editorKey === 'entry' && rule.action?.sound
    );

    const setEntryChime = (enabled: boolean) => {
        updateTransitPolicyRule('entry', (rule) => ({
            ...rule,
            action: {
                ...rule.action,
                sound: enabled ? 'bell' : undefined,
            },
        }));
    };

    // Contraband Settings
    const contrabandRule = transitPolicy?.rules?.find((rule) => rule.editorKey === 'contraband');
    const hasContrabandAlarm = !!(contrabandRule?.action?.sound);
    const hasContrabandBlock = !!(contrabandRule?.action?.lock);

    const setContrabandAlarm = (enabled: boolean) => {
        updateTransitPolicyRule('contraband', (rule) => {
            return {
                ...rule,
                action: {
                    ...rule.action,
                    sound: enabled ? 'alarm' : undefined,
                }
            };
        });
    };

    const setContrabandBlock = (enabled: boolean) => {
        updateTransitPolicyRule('contraband', (rule) => {
            return {
                ...rule,
                action: {
                    ...rule.action,
                    lock: enabled ? true : undefined,
                }
            };
        });
    };

    // Exit Settings
    const [exitRfidCodeList, setExitRfidCodeList] = useState<string[]>([]);
    const [exitPermission, setExitPermission] = useState<PERMISSION>(PERMISSION.DENY);
    const [presentSelectExitRfidModal, dismissSelectExitRfidModal] = useIonModal(SelectRfidModal, {
        deviceId,
        disabledRfidCodes: exitRfidCodeList,
        dismiss: (rfidCode: string, role: string) => {
            if (role === 'confirm') {
                addExitRfidCode(rfidCode);
            }
            dismissSelectExitRfidModal(rfidCode, role);
        },
    });

    const exitRule = transitPolicy?.rules?.find((rule) => rule.editorKey === 'exit');
    const hasExitRule = !!exitRule;

    const [exitTimeOption, setExitTimeOption] = useState<'anyTime' | 'specificTimes'>('anyTime');
    const [exitTimeRange, setExitTimeRange] = useState<{ start: string; end: string }>({
        start: '07:00',
        end: '18:00',
    });

    useEffect(() => {
        if (exitRule) {
            if (exitRule.enabled !== false) {
                if (exitRule.criteria?.rfidCode) {
                    setExitPermission(PERMISSION.ALLOWLIST);
                    setExitRfidCodeList(exitRule.criteria.rfidCode as string[]);
                } else {
                    setExitPermission(PERMISSION.ALLOW);
                }

                if (exitRule.criteria?.timeRange) {
                    setExitTimeOption('specificTimes');
                    const [start, end] = (exitRule.criteria.timeRange as string).split('-');
                    setExitTimeRange({ start, end });
                } else {
                    setExitTimeOption('anyTime');
                }
            } else {
                setExitPermission(PERMISSION.DENY);
                setExitTimeOption('anyTime');
            }
        } else {
            setExitPermission(PERMISSION.DENY);
            setExitTimeOption('anyTime');
        }
    }, [transitPolicy, exitRule]);

    const addExitRfidCode = (rfidCode: string) => {
        setExitRfidCodeList((prevList) => {
            const newList = [...prevList, rfidCode];
            if (exitPermission === PERMISSION.ALLOWLIST) {
                updateExitRuleCriteria({ rfidCode: newList });
            }
            return newList;
        });
    };

    const removeExitRfidCode = (rfidCode: string) => {
        setExitRfidCodeList((prevList) => {
            const newList = prevList.filter((code) => code !== rfidCode);
            if (exitPermission === PERMISSION.ALLOWLIST) {
                updateExitRuleCriteria({ rfidCode: newList });
            }
            return newList;
        });
    };

    const updateExitRuleCriteria = (criteriaUpdate: Partial<TransitPolicyRule['criteria']>) => {
        updateTransitPolicyRule('exit', (rule) => new TransitPolicyRule({
            ...rule,
            criteria: {
                ...rule.criteria,
                ...criteriaUpdate,
            },
        }));
    };

    const setExitPermissionState = (permission: PERMISSION) => {
        setExitPermission(permission);
        updateTransitPolicyRule('exit', (rule) => {
            let criteria: TransitPolicyRuleCriteria = { ...rule.criteria };
            if (permission === PERMISSION.ALLOWLIST) {
                criteria.rfidCode = exitRfidCodeList;
            } else {
                delete criteria.rfidCode;
            }
            return new TransitPolicyRule({
                ...rule,
                criteria,
                enabled: permission !== PERMISSION.DENY,
            });
        });
    };

    const setExitTimeOptionState = (option: 'anyTime' | 'specificTimes') => {
        setExitTimeOption(option);
        if (option === 'anyTime') {
            updateExitRuleCriteria({ timeRange: undefined });
        } else {
            updateExitRuleCriteria({ timeRange: `${exitTimeRange.start}-${exitTimeRange.end}` });
        }
    };

    const setExitTimeRangeState = (start: string, end: string) => {
        setExitTimeRange({ start, end });
        if (exitTimeOption === 'specificTimes') {
            updateExitRuleCriteria({ timeRange: `${start}-${end}` });
        }
    };

    // Power Loss Behavior Settings
    const setIdleLockBattery = (idleLockBattery: boolean) => {
        setTransitPolicy((prevPolicy) => {
            if (!prevPolicy) return prevPolicy;
            return new TransitPolicy({
                ...prevPolicy,
                idleLockBattery
            });
        });
    };

    const [presentJsonVieweModal, dismissJsonViewerModal] = useIonModal(JsonViewerModal, {
        data: () => cleanTransitPolicy(transitPolicy!),
        dismiss: () => dismissJsonViewerModal()
    });

    const showCode = async () => {
        presentJsonVieweModal({
            presentingElement: await modalController.getTop()
        });
    };

    const canCreatePolicy =
        transitPolicy &&
        !(
            (entryPermission === PERMISSION.ALLOWLIST && entryRfidCodeList.length === 0) ||
            (exitPermission === PERMISSION.ALLOWLIST && exitRfidCodeList.length === 0)
        );

    const selectedTemplate = selectedTemplateKey ? POLICY_TEMPLATE[selectedTemplateKey] : null;

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        {slideIndex !== PAGES.POLICY_TYPE && (
                            <IonButton onClick={() => swiperRef.current?.slidePrev()}>
                                <IonIcon icon={arrowBack} />
                            </IonButton>
                        )}
                    </IonButtons>
                    <IonTitle>{t('add_door_policy.title')}</IonTitle>
                    <IonButtons slot="primary">
                        <IonButton onClick={() => dismiss()}>
                            <IonIcon slot="icon-only" icon={close} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <Swiper
                className={styles.swiper}
                allowTouchMove={false}
                onSwiper={(swiper: SwiperClass) => {
                    swiperRef.current = swiper;
                }}
                onSlideChange={(swiper: SwiperClass) => setSlideIndex(swiper.activeIndex)}
            >
                <SwiperSlide className={styles.slide}>
                    <IonContent color="light">
                        <div className={styles.wrapper}>
                            <div className={styles.templateList}>
                                {Object.entries(POLICY_TEMPLATE).map(([key, template]) => (
                                    <IonItem
                                        key={key}
                                        button
                                        detail={false}
                                        lines="none"
                                        className={styles.cardItem}
                                        onClick={() => selectPolicyTemplate(key)}
                                    >
                                        <div className={styles.templateCard}>
                                            <span className={styles.templateIcon}>
                                                <IonIcon src={template.icon} aria-hidden="true" />
                                            </span>
                                            <div className={styles.templateBody}>
                                                <h2 className={styles.templateName}>{t(template.defaults.policyName)}</h2>
                                                <p className={styles.templateDescription}>{t(template.description)}</p>
                                            </div>
                                            <IonIcon icon={chevronForward} className={styles.templateChevron} aria-hidden="true" />
                                        </div>
                                    </IonItem>
                                ))}
                            </div>
                            <p className={styles.note}>{t('add_door_policy.help_text')}</p>
                        </div>
                    </IonContent>
                </SwiperSlide>

                <SwiperSlide className={styles.slide}>
                    <IonContent color="light">
                        <div className={styles.wrapper}>
                            {selectedTemplate && selectedTemplateKey !== 'CUSTOM' && (
                                <div className={styles.summaryCard}>
                                    <span className={styles.summaryIcon}>
                                        <IonIcon src={selectedTemplate.icon} aria-hidden="true" />
                                    </span>
                                    <div className={styles.templateBody}>
                                        <h2 className={styles.templateName}>{t(selectedTemplate.defaults.policyName)}</h2>
                                        <p className={styles.templateDescription}>{t(selectedTemplate.description)}</p>
                                    </div>
                                </div>
                            )}
                            {selectedTemplateKey === 'LOCKED' && (
                                <p className={styles.note}>{t('add_door_policy.notes.nothing_to_configure')}</p>
                            )}

                            {hasEntryRule && (
                                <PermissionSettings
                                    deviceId={deviceId}
                                    title={t('add_door_policy.sections.entry')}
                                    permission={entryPermission}
                                    setPermission={selectedTemplateKey !== 'UNLOCK_FOR_ALL' ? setEntryPermissionState : undefined}
                                    rfidCodeList={entryRfidCodeList}
                                    addRfidCode={async () =>
                                        presentSelectEntryRfidModal({
                                            presentingElement: await modalController.getTop(),
                                        })
                                    }
                                    removeRfidCode={removeEntryRfidCode}
                                    hasChime={hasEntryChime}
                                    setChime={setEntryChime}
                                />
                            )}

                            {contrabandRule && (
                                <div className={selectedTemplateKey === 'CUSTOM' ? styles.attachedCard : styles.section}>
                                    {selectedTemplateKey !== 'CUSTOM' && (
                                        <h3 className={styles.sectionLabel}>
                                            {t('add_door_policy.sections.contraband_detection')}
                                        </h3>
                                    )}
                                    <ContrabandSettings
                                        hasContrabandAlarm={hasContrabandAlarm}
                                        setContrabandAlarm={setContrabandAlarm}
                                        hasContrabandBlock={hasContrabandBlock}
                                        setContrabandBlock={setContrabandBlock}
                                    />
                                    {selectedTemplateKey === 'TRAINING' && hasContrabandBlock && (
                                        <p className={styles.note}>
                                            <strong>Note:</strong> {t('add_door_policy.notes.training_mode')}
                                        </p>
                                    )}
                                </div>
                            )}

                            {hasExitRule && (
                                <PermissionSettings
                                    deviceId={deviceId}
                                    title={t('add_door_policy.sections.exit')}
                                    permission={exitPermission}
                                    setPermission={setExitPermissionState}
                                    rfidCodeList={exitRfidCodeList}
                                    addRfidCode={async () =>
                                        presentSelectExitRfidModal({
                                            presentingElement: await modalController.getTop(),
                                        })
                                    }
                                    removeRfidCode={removeExitRfidCode}
                                    timeSettings={{
                                        exitTimeOption,
                                        setExitTimeOption: setExitTimeOptionState,
                                        exitTimeRange,
                                        setExitTimeRange: setExitTimeRangeState,
                                    }}
                                />
                            )}

                            {selectedTemplateKey === 'CUSTOM' && (
                                <section className={styles.section}>
                                    <h3 className={styles.sectionLabel}>{t('add_door_policy.sections.power_loss')}</h3>
                                    <IonList className={styles.listCard}>
                                        <IonItem lines="none">
                                            <IonToggle
                                                checked={!transitPolicy?.idleLockBattery}
                                                onIonChange={(e) => setIdleLockBattery(!e.detail.checked)}
                                            >
                                                <IonLabel>{t('add_door_policy.labels.unlock_on_power_loss')}</IonLabel>
                                            </IonToggle>
                                        </IonItem>
                                    </IonList>
                                    <p className={styles.note}>
                                        {t('add_door_policy.notes.power_loss')} <strong>{transitPolicy?.idleLockBattery ? t('add_door_policy.notes.locked') : t('add_door_policy.notes.unlocked')}</strong>.
                                    </p>
                                </section>
                            )}

                            <section className={styles.section}>
                                <h3 className={styles.sectionLabel}>{t('add_door_policy.sections.display')}</h3>
                                <IonList className={styles.listCard}>
                                    <IonItem lines="none">
                                        <IonInput
                                            label={t('add_door_policy.labels.policy_name')}
                                            maxlength={32}
                                            value={policyName}
                                            onIonChange={(e) => setPolicyName(e.detail.value!)}
                                        />
                                    </IonItem>
                                </IonList>
                            </section>

                            {transitPolicy && developerMode && (
                                <IonButton fill="clear" onClick={showCode} expand="full">
                                    {t('add_door_policy.labels.show_policy_code')}
                                </IonButton>
                            )}
                        </div>
                    </IonContent>
                </SwiperSlide>
            </Swiper>
            {slideIndex === PAGES.POLICY_NAME && (
                <ModalFooter>
                    <IonButton
                        expand="block"
                        className={footerStyles.primaryButton}
                        disabled={!canCreatePolicy}
                        onClick={() => createPolicy(true)}
                    >
                        {t('add_door_policy.labels.create_and_activate')}
                    </IonButton>
                    <IonButton
                        expand="block"
                        fill="clear"
                        className={footerStyles.ghostButton}
                        disabled={!canCreatePolicy}
                        onClick={() => createPolicy(false)}
                    >
                        {t('add_door_policy.labels.create_without_activating')}
                    </IonButton>
                </ModalFooter>
            )}
        </IonPage >
    );
};

interface AllowlistRfidItemProps {
    rfidCode: string;
    deviceId: string;
    onRemove: () => void;
}

const AllowlistRfidItem: React.FC<AllowlistRfidItemProps> = ({ rfidCode, deviceId, onRemove }) => {
    const query = useRfidProfileQuery(rfidCode, deviceId);
    const { t } = useTranslation('modals');

    return (
        <IonItem>
            <MicrochipItemLabel rfidCode={rfidCode} label={query.data?.label} isLoading={query.isLoading} />
            <RemoveIconButton
                slot="end"
                ariaLabel={t('add_door_policy.labels.remove_microchip', {
                    name: getRfidDisplayLabel(rfidCode, query.data?.label),
                })}
                onClick={onRemove}
            />
        </IonItem>
    );
};

interface PermissionSettingsProps {
    deviceId: string;
    title: string;
    permission: PERMISSION;
    setPermission?: (permission: PERMISSION) => void;
    rfidCodeList: string[];
    addRfidCode: () => void;
    removeRfidCode: (rfidCode: string) => void;
    hasChime?: boolean;
    setChime?: (enabled: boolean) => void;
    timeSettings?: {
        exitTimeOption: 'anyTime' | 'specificTimes';
        setExitTimeOption: (option: 'anyTime' | 'specificTimes') => void;
        exitTimeRange: { start: string; end: string };
        setExitTimeRange: (start: string, end: string) => void;
    };
}

const PermissionSettings: React.FC<PermissionSettingsProps> = ({
    deviceId,
    title,
    permission,
    setPermission,
    rfidCodeList,
    addRfidCode,
    removeRfidCode,
    hasChime,
    setChime,
    timeSettings,
}) => {
    const { t } = useTranslation('modals');
    const canConfigureTimeSettings = permission === PERMISSION.ALLOW || permission === PERMISSION.ALLOWLIST;

    return (
        <section className={styles.section}>
            <h3 className={styles.sectionLabel}>{title}</h3>
            {setPermission && (
                <IonList className={styles.listCard}>
                    <IonRadioGroup value={permission} onIonChange={(e) => setPermission(e.detail.value)}>
                        <IonItem>
                            <IonRadio value="allow" labelPlacement="end" justify="start">
                                {t('add_door_policy.permissions.allow_all')}
                            </IonRadio>
                        </IonItem>
                        <IonItem>
                            <IonRadio value="deny" labelPlacement="end" justify="start">
                                {t('add_door_policy.permissions.deny_all')}
                            </IonRadio>
                        </IonItem>
                        <IonItem lines={permission === PERMISSION.ALLOWLIST ? 'inset' : 'none'}>
                            <IonRadio value="allowlist" labelPlacement="end" justify="start">
                                {t('add_door_policy.permissions.specific_microchips')}
                            </IonRadio>
                        </IonItem>
                    </IonRadioGroup>
                    {permission === PERMISSION.ALLOWLIST && (
                        <>
                            {rfidCodeList.map((rfidCode) => (
                                <AllowlistRfidItem
                                    key={rfidCode}
                                    rfidCode={rfidCode}
                                    deviceId={deviceId}
                                    onRemove={() => removeRfidCode(rfidCode)}
                                />
                            ))}
                            <IonItem button detail={false} lines="none" onClick={addRfidCode}>
                                <IonIcon slot="start" icon={addOutline} color="primary" className={styles.addRowIcon} />
                                <IonLabel color="primary" className={styles.addRowLabel}>
                                    {t('add_door_policy.labels.add_microchip')}
                                </IonLabel>
                            </IonItem>
                        </>
                    )}
                </IonList>
            )}

            {hasChime !== undefined && setChime && (
                <IonList className={`${styles.listCard} ${setPermission ? styles.cardSpacing : ''}`}>
                    <IonItem lines="none">
                        <span slot="start" className={styles.rowIcon} aria-hidden="true">
                            <IonIcon icon={musicalNoteOutline} />
                        </span>
                        <IonToggle
                            checked={hasChime}
                            onIonChange={(e) => setChime(e.detail.checked)}
                            helperText={t('add_door_policy.notes.chime_help')}
                        >
                            {t('add_door_policy.labels.entry_chime')}
                        </IonToggle>
                    </IonItem>
                </IonList>
            )}

            {timeSettings && canConfigureTimeSettings && (
                <>
                    <IonList className={`${styles.listCard} ${styles.cardSpacing}`}>
                        <IonRadioGroup
                            value={timeSettings.exitTimeOption}
                            onIonChange={(e) => timeSettings.setExitTimeOption(e.detail.value)}
                        >
                            <IonItem>
                                <IonRadio value="anyTime" labelPlacement="end" justify="start">
                                    {t('add_door_policy.labels.any_time')}
                                </IonRadio>
                            </IonItem>
                            <IonItem lines={timeSettings.exitTimeOption === 'specificTimes' ? 'inset' : 'none'}>
                                <IonRadio value="specificTimes" labelPlacement="end" justify="start">
                                    {t('add_door_policy.labels.specific_times')}
                                </IonRadio>
                            </IonItem>
                        </IonRadioGroup>
                        {timeSettings.exitTimeOption === 'specificTimes' && (
                            <>
                                <IonItem>
                                    <IonLabel>{t('add_door_policy.labels.from')}</IonLabel>
                                    <IonDatetimeButton datetime="start-time" />
                                    <IonPopover keepContentsMounted={true}>
                                        <IonDatetime
                                            id="start-time"
                                            presentation="time"
                                            minuteValues="0,15,30,45"
                                            value={timeSettings.exitTimeRange.start}
                                            onIonChange={(e) =>
                                                timeSettings.setExitTimeRange(e.detail.value as string, timeSettings.exitTimeRange.end)
                                            }
                                        />
                                    </IonPopover>
                                </IonItem>
                                <IonItem lines="none">
                                    <IonLabel>{t('add_door_policy.labels.to')}</IonLabel>
                                    <IonDatetimeButton datetime="end-time" />
                                    <IonPopover keepContentsMounted={true}>
                                        <IonDatetime
                                            id="end-time"
                                            presentation="time"
                                            minuteValues="0,15,30,45"
                                            value={timeSettings.exitTimeRange.end}
                                            onIonChange={(e) =>
                                                timeSettings.setExitTimeRange(timeSettings.exitTimeRange.start, e.detail.value as string)
                                            }
                                        />
                                    </IonPopover>
                                </IonItem>
                            </>
                        )}
                    </IonList>
                    {timeSettings.exitTimeOption === 'specificTimes' && (
                        <p className={styles.note}>
                            {t('add_door_policy.notes.curfew', {
                                end: timeSettings.exitTimeRange.end,
                                start: timeSettings.exitTimeRange.start
                            })}
                        </p>
                    )}
                </>
            )}
        </section>
    );
};

interface ContrabandSettingsProps {
    hasContrabandAlarm: boolean;
    setContrabandAlarm: (enabled: boolean) => void;
    hasContrabandBlock: boolean;
    setContrabandBlock: (enabled: boolean) => void;
}

const ContrabandSettings: React.FC<ContrabandSettingsProps> = ({
    hasContrabandAlarm,
    setContrabandAlarm,
    hasContrabandBlock,
    setContrabandBlock,
}) => {
    const { t } = useTranslation('modals');

    return (
        <IonList className={styles.listCard}>
            <IonItem>
                <span slot="start" className={styles.rowIcon} aria-hidden="true">
                    <IonIcon icon={banOutline} />
                </span>
                <IonToggle
                    checked={hasContrabandBlock}
                    onIonChange={(e) => setContrabandBlock(e.detail.checked)}
                    helperText={t('add_door_policy.notes.block_contraband_help')}
                >
                    {t('add_door_policy.notes.block_contraband')}
                </IonToggle>
            </IonItem>
            <IonItem lines="none">
                <span slot="start" className={styles.rowIcon} aria-hidden="true">
                    <IonIcon icon={volumeHighOutline} />
                </span>
                <IonToggle
                    checked={hasContrabandAlarm}
                    onIonChange={(e) => setContrabandAlarm(e.detail.checked)}
                    helperText={t('add_door_policy.notes.contraband_alarm_help')}
                >
                    {t('add_door_policy.notes.contraband_alarm')}
                </IonToggle>
            </IonItem>
        </IonList>
    );
};

export default AddDoorPolicyModal;
